import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getNotifications,
  markNotificationAsRead,
  hideNotification as apiHideNotification,
  checkUnreadNotifications,
} from '@/app/api';

interface Notification {
  id: number;
  title: string;
  message: string;
  url?: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationsState {
  list: Notification[];
  currentPage: number;
  total: number;
  unRead: number;
  loading: boolean;
}

const initialState: NotificationsState = {
  list: [],
  currentPage: 1,
  total: 0,
  unRead: 0,
  loading: false,
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (page: number = 1, { rejectWithValue }) => {
    try {
      const response = await getNotifications(page);

      if (response?.last_seen_id) {
        await markNotificationAsRead(response?.last_seen_id);
      }

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (id: number, { rejectWithValue }) => {
    try {
      await markNotificationAsRead(id);
      return id;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const hideNotification = createAsyncThunk(
  'notifications/hideNotification',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiHideNotification(id);

      return id;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const checkUnread = createAsyncThunk(
  'notifications/checkUnread',
  async () => await checkUnreadNotifications()
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    markAllAsReadSync: (state) => {
      state.list = state.list.map((notif) => ({ ...notif, is_read: true }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;

        const { data, current_page, total } = action.payload;

        if (current_page === 1) {
          state.list = data;
        } else {
          state.list = [...state.list, ...data];
        }

        state.currentPage = current_page;
        state.total = total;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.loading = false;
      })
      .addCase(checkUnread.fulfilled, (state, action) => {
        state.unRead = action.payload.unread_count;
      })
      .addCase(hideNotification.fulfilled, (state, action) => {
        state.list = state.list.filter(
          (notification) => notification.id !== action.payload
        );
      });
  },
});

export const { markAllAsReadSync } = notificationsSlice.actions;
export default notificationsSlice.reducer;
